import React, { useEffect, useState } from 'react';
import Modal from '@mui/material/Modal';
import { Box, Typography, Button, TextField, Grid, CircularProgress } from '@mui/material';
import { UPDATE_CALENDAR, GET_CLASS_BY_ID, UPDATE_CLASS_DESCRIPTION } from '../../../graphql/branch';
import { useMutation, useQuery } from '@apollo/client';
import './modal-edit.scss';
import { showToast } from '../../../utils/toast';

interface ModalEditClassProps {
  open: boolean;
  handleClose: () => void;
  updateData: () => void;
  data: {
    id: number;
    start: string;
    end: string;
    availableSpace: number;
    description: string;
    classFit: {
      id: number;
      name: string;
    };
  };
}

const ModalEditClass: React.FC<ModalEditClassProps> = ({ open, handleClose, updateData, data }) => {
  const [className, setClassName] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [descriptionClass, setdescriptionClass] = useState('');

  const [updateCalendar, { loading: updateLoading, error: updateError }] = useMutation(UPDATE_CALENDAR);
  const [updateDescription] = useMutation(UPDATE_CLASS_DESCRIPTION);
  const { data: classData, loading: queryLoading, error: queryError } = useQuery(GET_CLASS_BY_ID, {
    variables: { class_id: data.classFit.id },
    skip: !open,
  });

  useEffect(() => {
    if (data) {
      setClassName(data.classFit.name || '');
      setStartTime(data.start ? data.start.slice(0, 16) : '');
      setEndTime(data.end ? data.end.slice(0, 16) : '');
      setdescriptionClass(data.description || '');
    }
  }, [data]);

  useEffect(() => {
    if (classData) {
      setdescriptionClass(classData.classById.description || '');
    }
  }, [classData]);

  const handleSave = async () => {
    if (!className || className.trim() === '') {
      showToast.warning('El nombre de la clase es requerido');
      return;
    }

    try {
      await updateCalendar({
        variables: {
          calendar: {
            calendar_id: data.id,
            start_time: `${startTime}:00-05:00`,
            end_time: `${endTime}:00-05:00`,
            description: descriptionClass
          }
        }
      });

      if (descriptionClass !== data.description || className !== data.classFit.name) {
        await updateDescription({
          variables: {
            classId: data.classFit.id,
            name: className,
            description: descriptionClass,
            description_html: '<p>'+descriptionClass+'<p>'
          }
        });
      }

      showToast.success('¡Clase actualizada exitosamente!');
      updateData();
      handleClose();
    } catch (e) {
      console.error("Error al ejecutar la mutación: ", e);
      showToast.error('Error al actualizar la clase. Por favor, intenta de nuevo.');
    }
  };

  if (updateLoading || queryLoading) return <CircularProgress />;
  if (queryError) return <p>Error al cargar la clase: {queryError.message}</p>;
  if (updateError) return <p>Error al actualizar el calendario: {updateError.message}</p>;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-new-feature-title"
      aria-describedby="modal-new-feature-description"
    >
      <Box className="modalStyle">
        <Typography id="modal-new-feature-title" variant="h6" component="h2">
          {data.classFit.name}
        </Typography>
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <TextField
              label="Nombre de la clase"
              fullWidth
              variant="outlined"
              value={className}
              onChange={(e) => setClassName(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Horario de Inicio"
              fullWidth
              type="datetime-local"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Horario de Fin"
              fullWidth
              type="datetime-local"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Descripción de la clase"
              fullWidth
              multiline
              minRows={3}
              maxRows={4}
              variant="outlined"
              value={descriptionClass}
              onChange={(e) => setdescriptionClass(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                style: {
                  maxHeight: '100px',
                  overflow: 'auto',
                  fontSize: '0.875rem',
                  padding: '10px',
                }
              }}
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Button onClick={handleClose} variant="contained">Cerrar</Button>
          <Button onClick={handleSave} variant="contained" color="primary">Guardar</Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ModalEditClass;
